import React, { useContext } from 'react';
import { ThemeContext } from '../../contexts/ThemeContext';

export interface KpiCardProps {
    icon: React.ReactNode;
    title: string;
    value: number | string | React.ReactNode;
    accentColor?: string;
    iconBg?: string;
    iconColor?: string;
    prefix?: React.ReactNode;
    suffix?: React.ReactNode;
    extra?: React.ReactNode;
    onClick?: () => void;
    valueFontSize?: number | string;
    style?: React.CSSProperties;
    className?: string;
}

export default function KpiCard({
    icon,
    title,
    value,
    accentColor = '#1677ff',
    iconBg,
    iconColor,
    prefix,
    suffix,
    extra,
    onClick,
    valueFontSize,
    style,
    className = '',
}: KpiCardProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    return (
        <div
            className={`modern-kpi-card ${isDark ? 'dark' : ''} ${className}`}
            onClick={onClick}
            style={{
                cursor: onClick ? 'pointer' : undefined,
                ...style,
            }}
        >
            <div
                className="modern-kpi-icon-wrap"
                style={{
                    background: iconBg || (isDark ? `${accentColor}24` : `${accentColor}12`),
                    border: `1px solid ${accentColor}30`,
                    color: iconColor || accentColor,
                }}
            >
                {icon}
            </div>
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 4,
                        marginBottom: 2,
                    }}
                >
                    <div
                        style={{
                            fontSize: 12.5,
                            fontWeight: 500,
                            color: isDark ? '#94a3b8' : '#64748b',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.3,
                        }}
                        title={title}
                    >
                        {title}
                    </div>
                    {extra && <div onClick={(e) => e.stopPropagation()}>{extra}</div>}
                </div>

                <div
                    style={{
                        fontSize: valueFontSize || 21,
                        fontWeight: 700,
                        color: isDark ? '#f8fafc' : '#0f172a',
                        letterSpacing: '-0.4px',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 3,
                    }}
                >
                    {prefix && (
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: isDark ? '#94a3b8' : '#64748b',
                                marginRight: 1,
                            }}
                        >
                            {prefix}
                        </span>
                    )}
                    <span>{value ?? 0}</span>
                    {suffix && (
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 500,
                                color: isDark ? '#94a3b8' : '#64748b',
                                marginLeft: 2,
                            }}
                        >
                            {suffix}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
